import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Warning, Newspaper, ArrowUpRight } from "@phosphor-icons/react";

/**
 * WeatherAlertsStrip — CMS-driven alerts + articles feed for the top of
 * the Weather Almanac. Pulls published pages tagged with `weather` /
 * `climate` / `alert` from the unified CMS.
 * Falls back to a curated "coming soon" state if nothing is tagged yet.
 */
const CURATED_FALLBACK = [
  { title: "Contribute your weather observations", url: "/observations", meta: "How-to · NaturePulse" },
  { title: "Weekly climate briefing", url: "/climate-change", meta: "Climate Hub" },
  { title: "Submit a storm sighting", url: "/#identify", meta: "Add Sighting" },
];

export default function WeatherAlertsStrip() {
  const [alerts, setAlerts] = useState(null);
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    // Alerts — CMS pages tagged 'weather-alert' OR 'alert' OR category='weather'
    http.get("/cms/pages?tag=weather-alert&limit=6")
      .then(({ data }) => setAlerts(data.pages || []))
      .catch(() => setAlerts([]));
    // Articles — published articles or news tagged 'weather' / 'climate'
    http.get("/cms/pages?tag=weather&limit=6")
      .then(({ data }) => setArticles(data.pages || []))
      .catch(() => setArticles([]));
  }, []);

  return (
    <section
      className="max-w-6xl mx-auto px-6 md:px-10 mt-14"
      data-testid="weather-alerts-strip"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="weather-alerts-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-red-700 flex items-center gap-1">
              <Warning size={12} weight="fill" /> Weather alerts
            </div>
            <Link to="/notifications" className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#8E8B7F)] hover:underline">All alerts <ArrowUpRight size={10} weight="bold" className="inline" /></Link>
          </div>
          {alerts === null ? (
            <div className="text-xs text-[#8E8B7F]">Loading…</div>
          ) : alerts.length === 0 ? (
            <ul className="space-y-2">
              {CURATED_FALLBACK.map((c) => (
                <li key={c.url}>
                  <Link to={c.url} className="block text-sm text-[#241209] hover:text-[color:var(--np-brand,#E4572E)]">
                    {c.title}
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] mt-0.5">{c.meta}</div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-2">
              {alerts.slice(0, 5).map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/${a.page_type}/${a.slug}`}
                    data-testid={`weather-alert-${a.slug}`}
                    className="block text-sm text-[#241209] hover:text-[color:var(--np-brand,#E4572E)]"
                  >
                    {a.title}
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] mt-0.5">
                      {a.published_at?.slice(0, 10)} · {a.category || "alert"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="weather-articles-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#E4572E)] flex items-center gap-1">
              <Newspaper size={12} weight="fill" /> Latest weather articles
            </div>
            <Link to="/articles" className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#8E8B7F)] hover:underline">All articles <ArrowUpRight size={10} weight="bold" className="inline" /></Link>
          </div>
          {articles === null ? (
            <div className="text-xs text-[#8E8B7F]">Loading…</div>
          ) : articles.length === 0 ? (
            <div className="text-xs text-[#8E8B7F] italic">Weather-tagged articles will appear here as editors publish them.</div>
          ) : (
            <ul className="space-y-2">
              {articles.slice(0, 5).map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/${a.page_type}/${a.slug}`}
                    data-testid={`weather-article-${a.slug}`}
                    className="block text-sm text-[#241209] hover:text-[color:var(--np-brand,#E4572E)]"
                  >
                    {a.title}
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#8E8B7F] mt-0.5">
                      {a.published_at?.slice(0, 10)} · {a.category || a.page_type}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
